import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge, CardPhoto, money } from "@/components/auction/AuctionUI";
import { OrderAction, OrderStageBadge, OrderSteps } from "@/components/auction/OrderStage";
import { Gavel } from "lucide-react";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { OrderRateButton } from "@/components/OrderRateButton";

const FILTERS = [["todo", "Todas"], ["accion", "Requieren acción"], ["ganadas", "Ganadas"], ["activas", "Pujando"], ["perdidas", "No ganadas"]];

const needsAction = (a) => a.is_winner && (!a.order || ["PENDIENTE_PAGO", "ENVIADO"].includes(a.order.status) || (a.offer && !a.offer.accepted));
const bucket = (a) => (a.status === "active" ? "activas" : a.is_winner ? "ganadas" : "perdidas");

function Row({ a, pendingRatings }) {
  return (
    <div data-testid={`bid-row-${a.id}`} className={`rounded-[20px] border p-3.5 ${needsAction(a) ? "border-[#FF4438]/45 bg-[#FF4438]/[.03]" : "border-white/12 bg-[#101010]"}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3.5">
        <div className="flex items-center gap-3.5 flex-1 min-w-0">
          <CardPhoto path={a.photos?.[0]} alt={a.product_name} w={42} h={56} size={200} rotate={0} glow="gray" />
          <div className="min-w-0 flex-1">
            <Link to={`/subastas/${a.id}`} className="font-extrabold leading-tight hover:underline">{a.product_name}</Link>
            <div className="text-xs text-white/55 mt-0.5">Tu puja máx. {money(a.my_max_bid)}{a.is_winner && a.offer ? ` · Ganaste por ${money(a.offer.amount)}` : ""}</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {a.status === "active" && <Badge tone={a.leading ? "green" : "coral"}>{a.leading ? "Vas ganando" : "Te superaron"}</Badge>}
              {a.status === "finalized" && !a.is_winner && <Badge tone="gray">No ganaste</Badge>}
              {a.is_winner && a.order && <OrderStageBadge order={a.order} testid={`bid-stage-${a.id}`} />}
              {a.is_winner && a.offer && !a.offer.accepted && <Badge tone="amber">Oferta de repechaje</Badge>}
            </div>
            {a.is_winner && a.order && <OrderSteps status={a.order.status} testid={`bid-steps-${a.id}`} />}
          </div>
        </div>
        {a.is_winner && <OrderAction order={a.order} auctionId={a.id} offer={a.offer} role="buyer" />}
        {a.status === "active" && <Link to={`/subastas/${a.id}`} className="sx-btn-ghost h-10 px-5 inline-flex items-center justify-center text-sm">Ver subasta</Link>}
      </div>
      {a.is_winner && a.order?.id && pendingRatings && <div className="mt-3"><OrderRateButton orderId={a.order.id} pendingList={pendingRatings} /></div>}
    </div>
  );
}

export function MisPujas() {
  const [list, setList] = useState(null);
  const [f, setF] = useState("todo");
  const [pendingRatings, setPendingRatings] = useState(null);
  const level = useLoadingLevel(!list);
  useEffect(() => {
    api.get("/auctions/participating").then((r) => setList(r.data.results || [])).catch(() => setList([]));
    api.get("/ratings/pending").then((r) => setPendingRatings(r.data || [])).catch(() => setPendingRatings([]));
  }, []);
  if (!list || level !== "none") return <HitSectionLoading level={level} loading={!list} section="inventory" className="mt-6" />;
  if (!list.length) return (
    <div className="sx-card p-10 text-center mt-7" data-testid="mis-pujas-empty">
      <Gavel className="mx-auto text-white/25 mb-3" size={32} />
      <p className="text-white/60 mb-5">Todavía no has pujado en ninguna subasta.</p>
      <Link to="/subastas" className="sx-btn inline-flex h-[52px] px-7 items-center">Ver subastas</Link>
    </div>
  );
  const n = (k) => (k === "todo" ? list.length : k === "accion" ? list.filter(needsAction).length : list.filter((a) => bucket(a) === k).length);
  const shown = f === "todo" ? list : f === "accion" ? list.filter(needsAction) : list.filter((a) => bucket(a) === f);
  return (
    <section className="mt-7" data-testid="mis-pujas">
      <div className="flex gap-2 overflow-x-auto -mx-5 px-5 lg:mx-0 lg:px-0 pb-1 mb-3">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF(k)} data-testid={`pujas-filter-${k}`}
                  className={`h-10 px-4 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${f === k ? "bg-white text-[#080808]" : "border border-white/15 text-white/80 hover:border-white/35"}`}>
            {l} {n(k)}
          </button>
        ))}
      </div>
      <div className="space-y-2.5">
        {shown.map((a) => <Row key={a.id} a={a} pendingRatings={pendingRatings} />)}
        {!shown.length && <p className="text-sm text-white/50 p-5">No tienes subastas en este filtro.</p>}
      </div>
    </section>
  );
}
